import { Spinner, Text } from "@aurem/ui";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { useStreamingDisplay } from "../hook/use-stream-effect.hook";

interface Props {
  stage?: string;
  statusText?: string;
}

export const StreamingIndicator: FC<Props> = ({ stage, statusText }) => {
  const { t } = useI18n();
  const { displayedReasoning: displayedStatus } = useStreamingDisplay(
    statusText,
    5,
  );

  // If we have custom status text, display it with typewriter effect
  if (statusText && statusText.trim().length > 0) {
    return (
      <div className="flex items-center gap-2">
        <Spinner size="2xs" />
        <div className="text-sm text-neutral-500 leading-relaxed flex-1 whitespace-pre-wrap">
          {displayedStatus}
          <ProcessingDots />
        </div>
      </div>
    );
  }

  let message: string;
  switch (stage) {
    case "EXECUTING_QUERY":
      message = t("apps.admin.chatAI.messages.processStates.analyzing");
      break;
    case "GENERATING_RESPONSE":
      message = t("apps.admin.chatAI.messages.processStates.generating");
      break;
    case "EXPORTING_RESULT":
      message = t("apps.admin.chatAI.messages.processStates.exportingResults");
      break;
    default:
      message = t("apps.admin.chatAI.messages.processStates.processing");
  }

  return (
    <div className="flex items-center gap-2 text-sm text-neutral-500">
      <Spinner size="2xs" />
      <Text size="sm">{message}</Text>
    </div>
  );
};

const ProcessingDots: FC = () => {
  const [dots, setDots] = useState(".");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "." : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return <span>{dots}</span>;
};

interface TypewriterTextProps {
  text: string;
  isComplete?: boolean;
  showCursor?: boolean;
}

export const TypewriterText: FC<TypewriterTextProps> = ({
  text,
  isComplete,
  showCursor = true,
}) => (
  <div className="text-sm text-neutral-500 leading-relaxed">
    {text || ""}
    {!isComplete && (
      <>
        <ProcessingDots />
        {showCursor && (
          <span className="inline-block w-2 h-4 ms-1 animate-pulse" />
        )}
      </>
    )}
  </div>
);
